import React, { useEffect } from 'react';
import { Text, styled } from '@volkswagen-onehub/components-core';
import { OneElementTemplate } from 'src/feature-app/Screen';
import { Email, PantallaNombreApellido, Telefono, CarlineSelect, ContactInfoWrapper } from 'src/components';
import { MapaEmpresas, EmpresaInfoComponent, ConfirmacionEmpresas, ThankYouEmpresas } from 'src/forms';
import { useDispatch, useStore } from 'react-redux';
import { dpsSend } from 'src/bff';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import {
  formatPrefix,
  FormController,
  removeWhiteSpace,
  useFaContent,
  useGetInstalaciones,
  useOneFormContext,
} from 'src/feature-app';
import { useInitializeLeadForm } from '../Leads';
import { Fleet } from './Fleet';
import { formatHeaders, formDataBuilder, getDpsFields } from '../format-fields';
import { useGetWaidLitera } from 'src/feature-app/hooks/use-get-waid-litera';

export enum EmpresasFormSteps {
  Modelo,
  Mapa,
  NombreApellido,
  EmailTelefono,
  Empresa,
  Fleet,
  Confirmacion,
}

export function EmpresasForm() {
  const { initializeForm } = useOneFormContext();
  const store = useStore();
  const installations = useGetInstalaciones('VW_DDB_FRM_VWEMPRESAS'); // Inicialización de las instalaciones
  const dispatch = useDispatch();
  const faContent = useFaContent();
  const waid = useGetWaidLitera();

  const defaultSteps: Steps[] = [
    {
      title: (
        <>
          <Text bold>¿Qué modelo </Text>te interesa para tu flota?
        </>
      ),
      fields: <OneElementTemplate element={<div style={{marginBottom: '2px'}}><CarlineSelect useCarline={true} /></div>} />,
      screenIndex: EmpresasFormSteps.Modelo,
      name: 'Modelo',
      outputs: ['modelo'],
      outputsText: ['modelo'],
    },
    {
      screenIndex: EmpresasFormSteps.Mapa,
      name: 'Mapa',
      multiStep: true,
      component: <MapaEmpresas formName="venta" showHorario={false} />,
    },
    {
      title: (
        <>
          Estamos deseando hacerte una propuesta. Pero antes, nos gustaría saber <Text bold>quién eres</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{marginBottom: '2px'}}><PantallaNombreApellido /></div>} allowOverflowingContent />,
      screenIndex: EmpresasFormSteps.NombreApellido,
      name: 'NombreApellido',
      outputs: ['name', 'surname', 'secondSurname'],
      outputsText: ['name', 'surname', 'secondSurname'],
      showOverflow: true,
    },
    {
      title: (
        <>
          <Text bold>¿En qué teléfono e email </Text>podemos localizarte?
        </>
      ),
      fields: (
        <OneElementTemplate
          element={
            <ContactInfoWrapper>
              <Telefono paginaConfirmacion={false} required={true} />
              <Email paginaConfirmacion={false} required={true} paddingTop='24px'/>
            </ContactInfoWrapper>
          }
        />
      ),
      screenIndex: EmpresasFormSteps.EmailTelefono,
      name: 'EmailTelefono',
      outputs: ['cellphone', 'email'],
      outputsText: ['cellphone', '/', 'email'],
    },
    {
      title: (
        <>
          Ahora, nos gustaría conocer algunos detalles sobre <Text bold>tu empresa</Text>
        </>
      ),
      fields: <OneElementTemplate element={<div style={{marginBottom: '2px'}}><EmpresaInfoComponent /></div>} />,
      screenIndex: EmpresasFormSteps.Empresa,
      name: 'Empresa',
      outputs: ['businessName', 'CIF', 'postalCode'],
      outputsText: ['businessName', '/', 'CIF', '/', 'postalCode'],
    },
    {
      title: (
        <>
          <Text bold>¿Cuántos coches </Text>necesitas para tu{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            flota?
            <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </>
      ),
      fields: <OneElementTemplate element={<Fleet />} />,
      screenIndex: EmpresasFormSteps.Fleet,
      name: 'Fleet',
      outputs: ['fleet'],
      outputsText: ['fleet'],
    },
    {
      fields: <ConfirmacionEmpresas />,
      screenIndex: EmpresasFormSteps.Confirmacion,
      name: 'Confirmacion',
      isLastStep: true,
      hidePreviousStep: true,
    },
  ];


  const EmpresasFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_VN_VO: 'VN',
      DESC_GDPR_ADAPTED: 'true',
      COD_FORM_ID: 'vw-oneform-empresas',
      DESC_CAMPAIGNBLOCK: '10. VW Empresas',
      DESC_FORMOBJECTIVE: 'Más Información',
      DESC_FORM_DETAIL: 'Empresas',
      DESC_ID_CAMPAIGN: '7010Y0000001rdOQAQ',
      DESC_WAID_LITERA: waid ? waid : 'B10C01_LDEMPR_WA1',
      DESC_TOKEN: 'VW_DDB_FRM_VWEMPRESAS',
    },
    fields: {
      formName: 'empresas',
      formType: 'contact',
      pageCategory: 'Más Información',
      formularioFlag: 'masInformacion',
    }, // Analítica
  };

  const sendForm = (): Promise<boolean> => {
    const { formData, dealersInfo }: OneFormState = store.getState();

    const { fields, headers } = formData;

    const { selectedInstallation, carlines } = dealersInfo;
    const {
      TXT_V_Installation_Data_Name__c,
      TXT_V_KVPS_code__c,
      FRML_TXT_codVentaProvincia__c,
      TXT_V_Installation_Code__c,
    } = selectedInstallation;

    return new Promise<any>(async (resolve, reject) => {
      const { tc, today, referalUrl, url, device } = getParamsForm();

      const data = {
        ...formatHeaders(headers), // Headers comunes a todos los formularios de leads
        'headers[DESC_URLORIGIN]': url,
        'headers[DESC_DEVICETYPE]': device,

        'headers[FECHA_INSERTION_DATE]': today,
        'headers[TC]': tc,
        'headers[DESC_GDPR_ADAPTED]': true,

        'fields[DESC_NAME]': fields.name,
        'fields[DESC_SURNAME_1]': fields.surname,
        'fields[DESC_SURNAME_2]': fields.secondSurname,
        'fields[DESC_EMAIL_ADDRESS]': removeWhiteSpace(fields.email),
        'fields[NUM_CELLPHONE]': `${formatPrefix(fields.prefix)}${fields.cellphone}`,
        'fields[DESC_BUSINESS_NAME]': fields.businessName,
        'fields[DESC_CIF]': fields.CIF,
        'fields[DESC_POSTAL_CODE]': fields.postalCode,
        'fields[DESC_FLEET]': fields.fleet,
        'fields[DESC_VEH_BRAND]': 'Volkswagen',
        'fields[DESC_FORM_VERSION]': `vw-oneform-empresas-${process.env.VERSION}`,
        'fields[DESC_DEALER_CODE_KVPS]': TXT_V_KVPS_code__c,
        'fields[DESC_DEALER_CODE]': TXT_V_Installation_Code__c,
        'fields[DESC_DEALER_PROVINCE]': fields.provinceName,
        'fields[COD_DEALER_PROVINCE]': FRML_TXT_codVentaProvincia__c,
        'fields[DESC_DEALER]': TXT_V_Installation_Data_Name__c,
        'fields[COD_INTEREST_CARLINE]': fields.carlineCode,
        'fields[DESC_INTEREST_CARLINE]': fields.modelo,
        'fields[DESC_LOPD]': 'PERMITE',
        'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO',
        'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
        'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
        ...getDpsFields(faContent),
      };

      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { loading, steps, firstStep, lastStep } = useInitializeLeadForm(defaultSteps);

  useEffect(() => {
    if (loading) {
      return;
    }

    const EmpresasFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      step: firstStep,
      steps: steps,
      notALayer: false,
      formType: 'layer',
      formTheme: 'inverted',
      stepsHistory: [0],
      thankYouComponent: <ThankYouEmpresas />,
    };

    initializeForm(EmpresasFormInfo, EmpresasFormData);
  }, [loading]);

  return loading ? null : <FormController steps={steps} screenType="layer-screen" />;
}
